import {
  FISHYMAP_POINT_ICON_SCALE_MAX,
  FISHYMAP_POINT_ICON_SCALE_MIN,
} from "./map-bridge-contract.js";
import { mapText } from "./map-i18n.js";
import { layerHighlightColorValue } from "./map-layer-highlight-style.js";
import {
  layerOpacityLabel,
  layerOpacityValue,
  pointIconScaleLabel,
  pointIconScaleValue,
} from "./map-layer-state.js";
import { resolveCdnAssetUrl } from "./map-item-icons.js";

function escapeAttribute(value) {
  return String(value ?? "").replace(/"/g, "&quot;");
}

function settingsDomToken(value) {
  return Array.from(String(value ?? ""))
    .map((character) => character.codePointAt(0).toString(16))
    .join("-");
}

function hoverFactIconMarkup(row, escapeHtml) {
  if (String(row?.swatchRgb || "").trim()) {
    return `<span class="fishymap-layer-fact-swatch" style="--fishymap-layer-fact-rgb:${escapeAttribute(row.swatchRgb)};" aria-hidden="true"></span>`;
  }
  return `<svg class="fishy-icon size-4" viewBox="0 0 24 24" aria-hidden="true"><use width="100%" height="100%" href="#fishy-${escapeAttribute(row?.icon || "information-circle")}"></use></svg>`;
}

function hoverFactValueMarkup(row, escapeHtml) {
  const value = String(row?.value || "").trim();
  if (!value) {
    return `<span class="text-base-content/35">${escapeHtml(mapText("layers.unavailable"))}</span>`;
  }
  return escapeHtml(value);
}

function appearanceTab(layer, lineagePanelVisible, escapeHtml) {
  const controls = [];
  const renderKey = [];
  if (!layer.locked) {
    renderKey.push(
      "opacity",
      Math.round(Number(layer.opacity) * 1000),
      Math.round(Number(layer.opacityDefault) * 1000),
    );
    controls.push(`
      <fieldset class="fishymap-layer-opacity-control fieldset">
        <div class="flex items-center justify-between gap-3">
          <span class="fieldset-legend m-0 px-0 text-[11px] uppercase tracking-[0.18em] text-base-content/45">${escapeHtml(mapText("layers.opacity"))}</span>
          <span class="text-xs font-semibold text-base-content/60" data-layer-opacity-value>${layerOpacityLabel(layer.opacity)}</span>
        </div>
        <input
          class="fishymap-layer-opacity-range range range-primary range-xs"
          data-layer-opacity="${escapeAttribute(layer.layerId)}"
          type="range"
          min="0"
          max="1"
          step="0.05"
          value="${layerOpacityValue(layer.opacity)}"
          aria-label="${escapeHtml(mapText("layers.opacity_for", { name: layer.name }))}"
        >
      </fieldset>
    `);
  }
  if (layer.supportsPointIcons || layer.supportsPointClustering) {
    if (layer.supportsPointIcons) {
      renderKey.push(
        "point-icons",
        layer.pointIconsVisible ? 1 : 0,
        Math.round(Number(layer.pointIconScale) * 1000),
        Math.round(Number(layer.pointIconScaleDefault) * 1000),
      );
    }
    if (layer.supportsPointClustering) {
      renderKey.push("point-clustering", layer.pointClusteringEnabled ? 1 : 0);
    }
    controls.push(`
      <fieldset class="fieldset">
        <span class="fieldset-legend m-0 px-0 text-[11px] uppercase tracking-[0.18em] text-base-content/45">${escapeHtml(mapText("layers.fish_evidence"))}</span>
        <div class="space-y-2">
          ${
            layer.supportsPointIcons
              ? `
                <label class="label cursor-pointer justify-start gap-3 py-0">
                  <input
                    class="toggle toggle-xs toggle-primary"
                    data-layer-point-icons="${escapeAttribute(layer.layerId)}"
                    type="checkbox"
                    ${layer.pointIconsVisible ? "checked" : ""}
                  >
                  <span class="text-xs text-base-content/70">${escapeHtml(mapText("layers.icons"))}</span>
                </label>
                <div class="space-y-2">
                  <div class="flex items-center justify-between gap-3">
                    <span class="text-xs font-semibold text-base-content/70">${escapeHtml(mapText("layers.fish_icon_size"))}</span>
                    <span class="text-xs font-semibold text-base-content/60" data-layer-point-icon-scale-value>${pointIconScaleLabel(layer.pointIconScale)}</span>
                  </div>
                  <input
                    class="range range-primary range-xs"
                    data-layer-point-icon-scale="${escapeAttribute(layer.layerId)}"
                    type="range"
                    min="${FISHYMAP_POINT_ICON_SCALE_MIN}"
                    max="${FISHYMAP_POINT_ICON_SCALE_MAX}"
                    step="0.05"
                    value="${pointIconScaleValue(layer.pointIconScale)}"
                    aria-label="${escapeHtml(mapText("layers.fish_icon_size_for", { name: layer.name }))}"
                  >
                </div>
              `
              : ""
          }
          ${
            layer.supportsPointClustering
              ? `
                <label class="label cursor-pointer justify-start gap-3 py-0">
                  <input
                    class="toggle toggle-xs toggle-primary"
                    data-layer-point-clustering="${escapeAttribute(layer.layerId)}"
                    type="checkbox"
                    ${layer.pointClusteringEnabled ? "checked" : ""}
                  >
                  <span class="text-xs text-base-content/70">${escapeHtml(mapText("layers.clustering"))}</span>
                </label>
              `
              : ""
          }
        </div>
      </fieldset>
    `);
  }
  if (layer.supportsConfidenceShowcase || layer.supportsCoordinateLineage) {
    if (layer.supportsConfidenceShowcase) {
      renderKey.push("confidence-showcase", layer.confidenceShowcase ? 1 : 0);
    }
    if (layer.supportsCoordinateLineage) {
      renderKey.push("lineage-panel", lineagePanelVisible ? 1 : 0);
    }
    controls.push(`
      <fieldset class="fieldset">
        <span class="fieldset-legend m-0 px-0 text-[11px] uppercase tracking-[0.18em] text-base-content/45">${escapeHtml(mapText("layers.confidence"))}</span>
        <div class="space-y-2">
          ${
            layer.supportsConfidenceShowcase
              ? `
                <label class="label cursor-pointer justify-start gap-3 py-0">
                  <input
                    class="toggle toggle-xs toggle-primary"
                    data-layer-confidence-showcase="${escapeAttribute(layer.layerId)}"
                    type="checkbox"
                    ${layer.confidenceShowcase ? "checked" : ""}
                  >
                  <span class="text-xs text-base-content/70">${escapeHtml(mapText("layers.confidence_showcase"))}</span>
                </label>
              `
              : ""
          }
          ${
            layer.supportsCoordinateLineage
              ? `
                <label class="label cursor-pointer justify-start gap-3 py-0">
                  <input
                    class="toggle toggle-xs toggle-primary"
                    data-layer-lineage-panel="${escapeAttribute(layer.layerId)}"
                    type="checkbox"
                    ${lineagePanelVisible ? "checked" : ""}
                  >
                  <span class="text-xs text-base-content/70">${escapeHtml(mapText("layers.lineage_panel"))}</span>
                </label>
              `
              : ""
          }
        </div>
      </fieldset>
    `);
  }
  return controls.length
    ? {
        id: "appearance",
        label: mapText("layers.tab.appearance"),
        renderKey,
        markup: controls.join(""),
      }
    : null;
}

function hoverTab(layer, hoverFactRows, sampleHoverVisible, escapeHtml) {
  if (!hoverFactRows.length && !layer.supportsPointIcons) {
    return null;
  }
  const controls = [];
  if (layer.supportsPointIcons) {
    controls.push(`
      <label class="label cursor-pointer justify-start gap-3 py-0">
        <input
          class="toggle toggle-xs toggle-primary"
          data-layer-sample-hover="${escapeAttribute(layer.layerId)}"
          type="checkbox"
          ${sampleHoverVisible ? "checked" : ""}
        >
        <span class="text-xs text-base-content/70">${escapeHtml(mapText("layers.sample_hover"))}</span>
      </label>
    `);
  }
  if (hoverFactRows.length) {
    controls.push(`
      <fieldset class="fieldset">
        <span class="fieldset-legend m-0 px-0 text-[11px] uppercase tracking-[0.18em] text-base-content/45">${escapeHtml(mapText("layers.hover_facts"))}</span>
        <div class="overflow-x-auto rounded-box border border-base-300/60 bg-base-100/60">
          <table class="table table-xs fishymap-layer-facts-table">
            <thead>
              <tr>
                <th class="w-8">${escapeHtml(mapText("layers.table.icon"))}</th>
                <th>${escapeHtml(mapText("layers.table.name"))}</th>
                <th>${escapeHtml(mapText("layers.table.fact"))}</th>
                <th class="w-14 text-right">${escapeHtml(mapText("layers.table.show"))}</th>
              </tr>
            </thead>
            <tbody>
              ${hoverFactRows
                .map(
                  (row) => `
                    <tr>
                      <td class="align-middle">${hoverFactIconMarkup(row, escapeHtml)}</td>
                      <td class="align-middle text-xs font-semibold text-base-content/80">${escapeHtml(row.name || row.label || row.key)}</td>
                      <td class="align-middle text-xs text-base-content/60">${hoverFactValueMarkup(row, escapeHtml)}</td>
                      <td class="align-middle text-right">
                        <input
                          class="toggle toggle-xs toggle-primary"
                          data-layer-hover-fact-layer-id="${escapeAttribute(layer.layerId)}"
                          data-layer-hover-fact-key="${escapeAttribute(row.key)}"
                          type="checkbox"
                          ${row.enabled ? "checked" : ""}
                          aria-label="${escapeHtml(mapText("layers.hover_show", {
                            fact: row.name || row.label || row.key,
                            layer: layer.name,
                          }))}"
                        >
                      </td>
                    </tr>
                  `,
                )
                .join("")}
            </tbody>
          </table>
        </div>
      </fieldset>
    `);
  }
  return {
    id: "hover",
    label: mapText("layers.tab.hover"),
    renderKey: [
      sampleHoverVisible ? 1 : 0,
      hoverFactRows.map((row) => [
        row.key,
        row.value,
        row.enabled ? 1 : 0,
        row.swatchRgb || "",
        row.icon || "",
        row.statusIcon || "",
        row.statusIconTone || "",
      ]),
    ],
    markup: controls.join(""),
  };
}

function highlightTab(layer, escapeHtml) {
  const highlight = layer.highlight?.current;
  if (!highlight) {
    return null;
  }
  const colorValue = layerHighlightColorValue(highlight.color);
  return {
    id: "highlight",
    label: mapText("layers.tab.highlight"),
    renderKey: [
      highlight.enabled ? 1 : 0,
      Math.round(Number(highlight.opacity) * 1000),
      colorValue,
    ],
    markup: `
      <label class="label cursor-pointer justify-start gap-3 py-0">
        <input
          class="toggle toggle-xs toggle-primary"
          data-layer-highlight-enabled="${escapeAttribute(layer.layerId)}"
          type="checkbox"
          ${highlight.enabled ? "checked" : ""}
        >
        <span class="text-xs text-base-content/70">${escapeHtml(mapText("layers.highlight.enabled"))}</span>
      </label>
      <fieldset class="fishymap-layer-highlight-opacity-control fieldset">
        <div class="flex items-center justify-between gap-3">
          <span class="fieldset-legend m-0 px-0 text-[11px] uppercase tracking-[0.18em] text-base-content/45">${escapeHtml(mapText("layers.highlight.opacity"))}</span>
          <span class="text-xs font-semibold text-base-content/60" data-layer-highlight-opacity-value>${layerOpacityLabel(highlight.opacity)}</span>
        </div>
        <input
          class="fishymap-layer-highlight-opacity-range range range-primary range-xs"
          data-layer-highlight-opacity="${escapeAttribute(layer.layerId)}"
          type="range"
          min="0"
          max="1"
          step="0.01"
          value="${layerOpacityValue(highlight.opacity)}"
          aria-label="${escapeHtml(mapText("layers.highlight.opacity_for", { name: layer.name }))}"
        >
      </fieldset>
      <label class="fishymap-layer-highlight-color-control">
        <span class="text-xs font-semibold text-base-content/70">${escapeHtml(mapText("layers.highlight.color"))}</span>
        <input
          class="fishymap-layer-highlight-color input input-sm"
          data-layer-highlight-color="${escapeAttribute(layer.layerId)}"
          type="color"
          value="${escapeAttribute(colorValue)}"
          aria-label="${escapeHtml(mapText("layers.highlight.color_for", { name: layer.name }))}"
        >
      </label>
    `,
  };
}

function waypointsTab(layer, escapeHtml) {
  const controls = [];
  const tradeNpcClasses = Array.isArray(layer.tradeNpcClasses) ? layer.tradeNpcClasses : [];
  if (tradeNpcClasses.length) {
    controls.push(`
      <fieldset class="fieldset">
        <span class="fieldset-legend m-0 px-0 text-[11px] uppercase tracking-[0.18em] text-base-content/45">${escapeHtml(mapText("layers.categories"))}</span>
        <div class="space-y-2">
          ${tradeNpcClasses.map((definition) => `
            <label class="label cursor-pointer justify-start gap-3 py-0">
              <input
                class="toggle toggle-xs toggle-primary"
                data-layer-trade-npc-class="${escapeAttribute(definition.id)}"
                type="checkbox"
                ${definition.visible ? "checked" : ""}
              >
              <img
                class="size-5 object-contain"
                src="${escapeAttribute(resolveCdnAssetUrl(definition.iconPath))}"
                alt=""
                aria-hidden="true"
              >
              <span class="text-xs text-base-content/70">${escapeHtml(definition.label)}</span>
            </label>
          `).join("")}
        </div>
      </fieldset>
    `);
  }
  if (layer.supportsWaypointConnections) {
    controls.push(`
      <label class="label cursor-pointer justify-start gap-3 py-0">
        <input
          class="toggle toggle-xs toggle-primary"
          data-layer-waypoint-connections="${escapeAttribute(layer.layerId)}"
          type="checkbox"
          ${layer.waypointConnectionsVisible ? "checked" : ""}
        >
        <span class="text-xs text-base-content/70">${escapeHtml(mapText("layers.connections"))}</span>
      </label>
    `);
  }
  if (layer.supportsWaypointLabels) {
    controls.push(`
      <label class="label cursor-pointer justify-start gap-3 py-0">
        <input
          class="toggle toggle-xs toggle-primary"
          data-layer-waypoint-labels="${escapeAttribute(layer.layerId)}"
          type="checkbox"
          ${layer.waypointLabelsVisible ? "checked" : ""}
        >
        <span class="text-xs text-base-content/70">${escapeHtml(mapText("layers.names"))}</span>
      </label>
    `);
  }
  return controls.length
    ? {
        id: "waypoints",
        label: mapText("layers.tab.waypoints"),
        renderKey: [
          tradeNpcClasses.map((definition) => [definition.id, definition.visible ? 1 : 0]),
          layer.waypointConnectionsVisible ? 1 : 0,
          layer.waypointLabelsVisible ? 1 : 0,
        ],
        markup: `<div class="space-y-2">${controls.join("")}</div>`,
      }
    : null;
}

export function buildLayerSettingsView({
  layer,
  hoverFactRows = [],
  sampleHoverVisible = true,
  lineagePanelVisible = false,
  requestedTab = "",
  escapeHtml = (value) => String(value ?? ""),
} = {}) {
  if (!layer || typeof layer !== "object") {
    return { activeTab: "", renderKey: [], markup: "" };
  }
  const tabs = [
    appearanceTab(layer, lineagePanelVisible, escapeHtml),
    hoverTab(layer, hoverFactRows, sampleHoverVisible, escapeHtml),
    highlightTab(layer, escapeHtml),
    waypointsTab(layer, escapeHtml),
  ].filter(Boolean);
  if (!tabs.length) {
    return { activeTab: "", renderKey: [], markup: "" };
  }
  const normalizedRequestedTab = String(requestedTab ?? "").trim();
  const activeTab = tabs.find((tab) => tab.id === normalizedRequestedTab) || tabs[0];
  const token = settingsDomToken(layer.layerId);
  const tabId = (tab) => `fishymap-layer-settings-tab-${token}-${tab.id}`;
  const panelId = (tab) => `fishymap-layer-settings-panel-${token}-${tab.id}`;
  return {
    activeTab: activeTab.id,
    renderKey: [
      activeTab.id,
      tabs.map((tab) => [tab.id, tab.renderKey]),
    ],
    markup: `
      <div class="fishymap-layer-settings">
        <div
          class="fishymap-layer-settings-tabs tabs tabs-box"
          role="tablist"
          aria-label="${escapeHtml(mapText("layers.settings_show", { name: layer.name }))}"
        >
          ${tabs
            .map((tab) => {
              const active = tab.id === activeTab.id;
              return `
                <button
                  id="${tabId(tab)}"
                  class="fishymap-layer-settings-tab tab ${active ? "tab-active" : ""}"
                  type="button"
                  role="tab"
                  aria-selected="${active ? "true" : "false"}"
                  aria-controls="${panelId(tab)}"
                  tabindex="${active ? "0" : "-1"}"
                  data-layer-settings-layer-id="${escapeAttribute(layer.layerId)}"
                  data-layer-settings-tab="${escapeAttribute(tab.id)}"
                >
                  ${escapeHtml(tab.label)}
                </button>
              `;
            })
            .join("")}
        </div>
        <div
          id="${panelId(activeTab)}"
          class="fishymap-layer-settings-panel fishymap-layer-controls"
          role="tabpanel"
          aria-labelledby="${tabId(activeTab)}"
          data-layer-settings-panel="${escapeAttribute(activeTab.id)}"
        >
          ${activeTab.markup}
        </div>
      </div>
    `,
  };
}
